import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import { getTriggeredActions } from './dataProtectionQuestions';
import { agreementTypeLabel, type AgreementType } from './legalLinks';
import '../initiation/PocTypePage.scss';
import apiService from '../../service/ApiService';

type SuccessCriterion = { criterion?: string; measurement?: string };

type LocationState = {
  pocId?: string;
  successCriteria?: SuccessCriterion[];
  environment?: {
    siteType?: 'CUSTOMER_FACING' | 'ISS_INTERNAL';
    testSiteAvailable?: boolean | 'NOT_NEEDED';
  };
  nda?: {
    sharingConfidentialInfo?: boolean;
    signedNdaFileName?: string | null;
    confirmed?: boolean;
  };
  dataProtection?: {
    dpaRequired?: boolean;
    piaRequired?: boolean;
    tiaRequired?: boolean;
  };
  itSecurity?: {
    riskBand?: string;
    bandTitle?: string;
    bandMessage?: string;
  };
  legal?: {
    agreementType?: AgreementType;
    signedAgreementFileName?: string | null;
    contractCoverFileName?: string | null;
    declarationAccepted?: boolean;
  };
  procurement?: {
    noCommitmentDeclared?: boolean;
    sourcingWillFollowDeclared?: boolean;
    complianceConfirmed?: boolean;
  };
};

const yesNo = (value: boolean | undefined) =>
  value === true ? 'Yes' : value === false ? 'No' : '—';

const PreparationReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!state.procurement?.complianceConfirmed) {
    return (
      <Section theme="sand" spacing="tight">
        <Eyebrow>POC Preparation</Eyebrow>
        <Headline as="h1" size="3xl">
          Review and submit
        </Headline>
        <Lead>Complete Procurement first.</Lead>
        <button
          type="button"
          className="initiation__next"
          onClick={() =>
            navigate('/pocs/new/preparation/procurement', { state })
          }
        >
          Go to Procurement
        </button>
      </Section>
    );
  }

  const criteria = state.successCriteria ?? [];
  const env = state.environment;
  const nda = state.nda;
  const dp = state.dataProtection;
  const its = state.itSecurity;
  const legal = state.legal;
  const procurement = state.procurement;

  const dpActions =
    dp &&
    getTriggeredActions({
      dpaRequired: dp.dpaRequired === true,
      piaRequired: dp.piaRequired === true,
      tiaRequired: dp.tiaRequired === true,
    });

  const testSiteLabel =
    env?.testSiteAvailable === 'NOT_NEEDED'
      ? 'No need for a test site'
      : env?.testSiteAvailable === true
        ? 'Yes'
        : env?.testSiteAvailable === false
          ? 'No'
          : '—';

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!pocId) {
      setSubmitError('Missing PoC id. Complete Initiation first.');
      return;
    }
    if (!declarationAccepted) {
      setSubmitError('Confirm the declaration before submitting.');
      return;
    }
    setIsSubmitting(true);
    try {
      await apiService.completePreparation(pocId, {
        declarationAccepted: true,
      });
      setSubmitted(true);
      navigate('/pocs/new/running/workspace', { state });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not complete preparation. Check Network tab (all prep steps must already be saved).'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="initiation">
      <aside className="initiation__phases" aria-label="PoC phases">
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">1</span>
          <span>Initiation</span>
        </div>
        <div className="initiation__phase initiation__phase--active">
          <span className="initiation__phase-num">2</span>
          <span>Preparation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">3</span>
          <span>Running</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">4</span>
          <span>Evaluation</span>
        </div>
      </aside>

      <div className="initiation__main">
        <nav className="initiation__steps" aria-label="Preparation steps">
          <span className="initiation__step">Step 1</span>
          <span className="initiation__step">Step 2</span>
          <span className="initiation__step">Step 3</span>
          <span className="initiation__step">Step 4</span>
          <span className="initiation__step">Step 5</span>
          <span className="initiation__step">Step 6</span>
          <span className="initiation__step">Step 7</span>
          <span className="initiation__step initiation__step--active">
            Review and Submit
          </span>
        </nav>

        <Section theme="light" spacing="tight">
          <Eyebrow>POC Preparation</Eyebrow>
          <Headline as="h1" size="3xl">
            Review and submit
          </Headline>
          <Lead>
            Check your Preparation answers, then submit to complete this phase and move to the next phase.
          </Lead>

          <div className="review-grid">
            <article className="review-card">
              <h3>Success criteria</h3>
              {criteria.length === 0 ? (
                <p>—</p>
              ) : (
                criteria.map((row, index) => (
                  <p key={index}>
                    <strong>{index + 1}.</strong> {row.criterion || '—'}
                    <br />
                    <em>Evaluate:</em> {row.measurement || '—'}
                  </p>
                ))
              )}
            </article>

            <article className="review-card">
              <h3>PoC environment</h3>
              <p>
                <strong>Site:</strong>{' '}
                {env?.siteType === 'ISS_INTERNAL'
                  ? 'ISS-Internal'
                  : env?.siteType === 'CUSTOMER_FACING'
                    ? 'Customer-facing'
                    : '—'}
              </p>
              <p>
                <strong>Test site available:</strong> {testSiteLabel}
              </p>
            </article>

            <article className="review-card">
              <h3>NDA</h3>
              <p>
                <strong>Sharing confidential info:</strong>{' '}
                {yesNo(nda?.sharingConfidentialInfo)}
              </p>
              {nda?.sharingConfidentialInfo ? (
                <p>
                  <strong>Signed NDA:</strong>{' '}
                  {nda.signedNdaFileName || '—'}
                </p>
              ) : null}
              <p>
                <strong>Confirmed:</strong> {yesNo(nda?.confirmed)}
              </p>
            </article>

            <article className="review-card">
              <h3>Data protection</h3>
              {dpActions && dpActions.length > 0 ? (
                <ul className="phase-gate__list">
                  {dpActions.map((action) => (
                    <li key={action}>{action}</li>
                  ))}
                </ul>
              ) : (
                <p>No action required</p>
              )}
            </article>

            <article className="review-card">
              <h3>IT security</h3>
              <p>{its?.bandTitle ?? its?.riskBand ?? '—'}</p>
              <p>{its?.bandMessage}</p>
            </article>

            <article className="review-card">
              <h3>Legal</h3>
              <p>
                <strong>Agreement:</strong>{' '}
                {legal?.agreementType
                  ? agreementTypeLabel(legal.agreementType)
                  : '—'}
              </p>
              <p>
                <strong>Signed agreement:</strong>{' '}
                {legal?.signedAgreementFileName || '—'}
              </p>
              <p>
                <strong>Contract Cover:</strong>{' '}
                {legal?.contractCoverFileName || '—'}
              </p>
              <p>
                <strong>Declaration:</strong>{' '}
                {yesNo(legal?.declarationAccepted)}
              </p>
            </article>

            <article className="review-card">
              <h3>Procurement</h3>
              <p>
                <strong>No commercial commitments:</strong>{' '}
                {yesNo(procurement?.noCommitmentDeclared)}
              </p>
              <p>
                <strong>Sourcing if successful:</strong>{' '}
                {yesNo(procurement?.sourcingWillFollowDeclared)}
              </p>
              <p>
                <strong>Compliance confirmed:</strong>{' '}
                {yesNo(procurement?.complianceConfirmed)}
              </p>
            </article>
          </div>

          <label className="score-result__confirm">
            <input
              type="checkbox"
              checked={declarationAccepted}
              onChange={(e) => setDeclarationAccepted(e.target.checked)}
              disabled={isSubmitting || submitted}
            />
            <span>
              I confirm that all Preparation steps are complete and accurate,
              and I am ready to move this PoC to Running.
            </span>
          </label>

          {submitError ? (
            <p className="bj-field__error">{submitError}</p>
          ) : null}

          {submitted ? (
            <p className="scorecard__notice" role="status">
              Preparation submitted.
            </p>
          ) : null}

          <div className="initiation__actions initiation__actions--wrap">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() =>
                navigate('/pocs/new/preparation/procurement', { state })
              }
            >
              ← Back to Procurement
            </button>

            <div className="score-result__buttons">
              <button
                type="button"
                className="initiation__next initiation__next--secondary"
                onClick={() => navigate('/')}
              >
                Back to home
              </button>
              <button
                type="button"
                className="initiation__next"
                disabled={submitted || isSubmitting || !declarationAccepted}
                onClick={handleSubmit}
              >
                {isSubmitting ? 'Submitting...' : 'Submit preparation'}
              </button>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default PreparationReviewPage;
